import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  TooltipContent as RadixTooltipContent,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface InfoTooltipProps
  extends React.ComponentPropsWithoutRef<typeof RadixTooltipContent> {
  /** Element that triggers the tooltip (usually the info icon) */
  trigger: React.ReactElement;
  /** Tooltip body */
  children: React.ReactNode;
  /** Hover delay before showing tooltip (ms). Defaults to 150. */
  delayDuration?: number;
  /** Suppress hover-open right after mount to avoid auto-open under cursor (ms). Defaults to 300. */
  suppressInitialHoverMs?: number;
}

/**
 * InfoTooltip wraps Radix Tooltip to support two modes:
 * 1. Hover — appears while the pointer is over the trigger & hides on leave.
 * 2. Click (pinned) — clicking the trigger pins the tooltip open. It remains
 *    visible until the user clicks anywhere outside the tooltip or trigger.
 *
 * When pinned, an additional shadow is added so users can tell it is latched.
 */
export function InfoTooltip({
  trigger,
  children,
  sideOffset = 4,
  delayDuration = 150,
  suppressInitialHoverMs = 300,
  ...contentProps
}: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [hoverEnabled, setHoverEnabled] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Close when clicking outside if pinned
  useEffect(() => {
    if (!pinned) return;

    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (
        !triggerRef.current?.contains(target) &&
        !contentRef.current?.contains(target)
      ) {
        setPinned(false);
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleOutsideClick);
    return () =>
      document.removeEventListener("pointerdown", handleOutsideClick);
  }, [pinned]);

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setPinned((prev) => {
      const nextPinned = !prev;
      setOpen(nextPinned);
      return nextPinned;
    });
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      // Allow Radix to open on hover/focus, but prevent it from closing when
      // pinned is true.
      if (!pinned) {
        if (nextOpen && !hoverEnabled) return; // suppress initial hover-open
        setOpen(nextOpen);
      }
    },
    [pinned, hoverEnabled],
  );

  // Enable hover after a short delay to avoid opening immediately on mount
  useEffect(() => {
    setHoverEnabled(false);
    const t = setTimeout(() => setHoverEnabled(true), suppressInitialHoverMs);
    return () => clearTimeout(t);
    // Re-run when content re-mounts
  }, [suppressInitialHoverMs]);

  return (
    <TooltipProvider>
      <Tooltip
        open={open}
        onOpenChange={handleOpenChange}
        delayDuration={delayDuration}
      >
        <TooltipTrigger
          asChild
          ref={(node) => {
            triggerRef.current = node as HTMLElement | null;
          }}
          onClick={handleTriggerClick}
        >
          {trigger}
        </TooltipTrigger>
        <RadixTooltipContent
          ref={contentRef}
          sideOffset={sideOffset}
          {...contentProps}
          className={cn(
            "z-50 w-72 rounded-lg corner-smooth-lg border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            pinned && "shadow-lg",
            contentProps.className,
          )}
        >
          {children}
        </RadixTooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
